import { useId, type ReactNode } from "react";
import { Link, router } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { Label } from "@/components/ui/label";

export type CaseSection = {
  id: string;
  label: string;
  href: string;
  complete?: boolean;
  disabled?: boolean;
};

export function CaseNavigation({
  items,
  active,
  label,
  preserveState = false,
}: {
  items: CaseSection[];
  active: string;
  label: string;
  preserveState?: boolean;
}) {
  const selectId = useId();
  return (
    <nav className="case-navigation" aria-label={label}>
      <div className="case-navigation-select flex flex-col gap-2">
        <Label htmlFor={selectId}>{label}</Label>
        <NativeSelect
          id={selectId}
          value={active}
          onChange={(event) => {
            const item = items.find(
              (candidate) => candidate.id === event.target.value,
            );
            if (item && !item.disabled)
              router.visit(item.href, { preserveState });
          }}
        >
          {items.map((item) => (
            <NativeSelectOption
              key={item.id}
              value={item.id}
              disabled={item.disabled}
            >
              {item.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
      <div className="case-navigation-links">
        {items.map((item) =>
          item.disabled ? (
            <span
              key={item.id}
              className="case-navigation-disabled"
              aria-disabled="true"
            >
              {item.label}
            </span>
          ) : (
            <Link
              key={item.id}
              href={item.href}
              preserveState={preserveState}
              aria-current={item.id === active ? "page" : undefined}
            >
              <span>{item.label}</span>
              {item.complete && (
                <Check aria-hidden="true" className="size-4 shrink-0" />
              )}
            </Link>
          ),
        )}
      </div>
    </nav>
  );
}

export function CaseFacts({
  facts,
}: {
  facts: { label: string; value: ReactNode }[];
}) {
  return (
    <dl className="case-facts">
      {facts.map((fact) => (
        <div key={fact.label}>
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function CaseWorkspace({
  context,
  navigation,
  children,
  fluid = false,
  editor = true,
  className,
}: {
  context?: ReactNode;
  navigation?: ReactNode;
  children: ReactNode;
  fluid?: boolean;
  editor?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={cn("case-workspace", className)}>
      {context && (
        <aside
          className="case-context"
          aria-label={t("product_ux.workspace.context")}
        >
          {context}
        </aside>
      )}
      <div
        className="case-workspace-body"
        data-layout-container=""
        data-with-navigation={Boolean(navigation)}
      >
        {navigation}
        <div
          className="case-workspace-content"
          data-layout-frame=""
          data-case-editor={editor}
          data-fluid={fluid}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
